import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Plus, Trash2, Save, Loader2 } from "lucide-react";
import { toast } from "sonner";

const EMPTY = { name: "", role: "", org: "", quote: "", active: true, order: 0 };

function TestimonialRow({ t, onSaved, onDeleted }) {
  const [form, setForm] = useState(t);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.patch(`/admin/testimonials/${t.id}`, { name: form.name, role: form.role, org: form.org, quote: form.quote, active: form.active, order: Number(form.order) || 0 });
      onSaved(data); toast.success("Testimonial saved");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  const remove = async () => {
    if (!window.confirm("Delete this testimonial?")) return;
    await api.delete(`/admin/testimonials/${t.id}`); onDeleted(t.id); toast.success("Deleted");
  };
  return (
    <div className="border border-border rounded-2xl p-5 bg-card space-y-3" data-testid={`admin-testimonial-${t.id}`}>
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          {t.placeholder && <Badge variant="secondary" className="bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300 border-0">Starter quote — edit me</Badge>}
          <Badge variant={form.active ? "default" : "outline"}>{form.active ? "Shown" : "Hidden"}</Badge>
        </div>
        <div className="flex items-center gap-2 text-sm">
          <Label htmlFor={`act-${t.id}`} className="text-xs text-muted-foreground">Show on site</Label>
          <Switch id={`act-${t.id}`} checked={form.active} onCheckedChange={(v) => setForm((f) => ({ ...f, active: v }))} data-testid={`admin-testimonial-active-${t.id}`} />
        </div>
      </div>
      <div className="grid sm:grid-cols-3 gap-3">
        <Input value={form.name} onChange={set("name")} placeholder="Name" data-testid={`admin-testimonial-name-${t.id}`} />
        <Input value={form.role} onChange={set("role")} placeholder="Role (e.g. Registered Manager)" />
        <Input value={form.org} onChange={set("org")} placeholder="Organisation / setting" />
      </div>
      <Textarea value={form.quote} onChange={set("quote")} rows={3} placeholder="Quote" data-testid={`admin-testimonial-quote-${t.id}`} />
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2"><Label className="text-xs text-muted-foreground">Order</Label><Input type="number" value={form.order} onChange={set("order")} className="w-20 h-8" /></div>
        <div className="flex gap-2">
          <Button variant="ghost" size="sm" onClick={remove} data-testid={`admin-testimonial-delete-${t.id}`}><Trash2 className="w-4 h-4 mr-1" /> Delete</Button>
          <Button size="sm" onClick={save} disabled={busy} data-testid={`admin-testimonial-save-${t.id}`}>{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4 mr-1" /> Save</>}</Button>
        </div>
      </div>
    </div>
  );
}

export default function AdminTestimonials() {
  const [items, setItems] = useState([]);
  const [draft, setDraft] = useState(EMPTY);
  const [adding, setAdding] = useState(false);
  useEffect(() => { api.get("/admin/testimonials").then((r) => setItems(r.data)); }, []);

  const add = async (e) => {
    e.preventDefault(); setAdding(true);
    try {
      const { data } = await api.post("/admin/testimonials", { ...draft, order: Number(draft.order) || items.length + 1 });
      setItems((l) => [...l, data]); setDraft(EMPTY); toast.success("Testimonial added");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } finally { setAdding(false); }
  };

  return (
    <div className="space-y-6" data-testid="admin-testimonials">
      <p className="text-sm text-muted-foreground">These quotes appear on the landing page. Starter quotes are only flagged here — edit or replace them with real feedback.</p>
      <div className="space-y-4">
        {items.map((t) => (
          <TestimonialRow key={t.id} t={t}
            onSaved={(d) => setItems((l) => l.map((x) => (x.id === d.id ? d : x)))}
            onDeleted={(id) => setItems((l) => l.filter((x) => x.id !== id))} />
        ))}
      </div>
      <form onSubmit={add} className="border border-dashed border-border rounded-2xl p-5 space-y-3" data-testid="admin-testimonial-new-form">
        <p className="font-semibold text-sm flex items-center gap-1.5"><Plus className="w-4 h-4" /> Add a testimonial</p>
        <div className="grid sm:grid-cols-3 gap-3">
          <Input required value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} placeholder="Name" data-testid="admin-testimonial-new-name" />
          <Input value={draft.role} onChange={(e) => setDraft({ ...draft, role: e.target.value })} placeholder="Role" />
          <Input value={draft.org} onChange={(e) => setDraft({ ...draft, org: e.target.value })} placeholder="Organisation" />
        </div>
        <Textarea required minLength={10} rows={3} value={draft.quote} onChange={(e) => setDraft({ ...draft, quote: e.target.value })} placeholder="Quote" data-testid="admin-testimonial-new-quote" />
        <Button type="submit" size="sm" disabled={adding} data-testid="admin-testimonial-new-submit">{adding ? <Loader2 className="w-4 h-4 animate-spin" /> : "Add testimonial"}</Button>
      </form>
    </div>
  );
}
